import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  Alert,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Room as RoomIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const ClassSchedule = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [todaySchedule, setTodaySchedule] = useState([]);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const timeSlots = [
    '08:00-09:00', '09:00-10:00', '10:00-11:00', '11:00-12:00',
    '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00'
  ];

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/schedule');
      if (response.data.success) {
        // Handle the new API response structure
        const scheduleData = response.data.data;
        const fullSchedule = scheduleData.fullSchedule || [];
        const todaySchedule = scheduleData.todaySchedule || [];
        
        setSchedule(fullSchedule);
        setTodaySchedule(todaySchedule);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch class schedule', { variant: 'error' });
      console.error('Schedule fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTodaySchedule = (scheduleData) => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    const todayClasses = scheduleData.filter(item => item.day_of_week === today);
    setTodaySchedule(todayClasses.sort((a, b) => a.start_time.localeCompare(b.start_time)));
  };

  const getScheduleByDay = (day) => {
    return schedule
      .filter(item => item.day_of_week === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getCurrentClass = () => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    
    return todaySchedule.find(cls => 
      cls.start_time <= currentTime && cls.end_time >= currentTime
    );
  };

  const getNextClass = () => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    
    return todaySchedule.find(cls => cls.start_time > currentTime);
  };

  const isCurrentClass = (classItem) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    
    return classItem.day_of_week === today && 
           classItem.start_time <= currentTime && 
           classItem.end_time >= currentTime;
  };

  const isUpcomingClass = (classItem) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);
    const today = now.toLocaleDateString('en-US', { weekday: 'long' });
    
    return classItem.day_of_week === today && classItem.start_time > currentTime;
  };

  const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        sx={{ 
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          border: `1px solid ${color}30`,
          height: '100%',
          cursor: onClick ? 'pointer' : 'default'
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: color }}>
                {value}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const currentClass = getCurrentClass();
  const nextClass = getNextClass();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Class Schedule
        </Typography>
        <IconButton onClick={fetchSchedule} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Current Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Today's Classes" 
            value={todaySchedule.length} 
            icon={<TodayIcon />} 
            color="#2196F3"
            subtitle={`${new Date().toLocaleDateString('en-US', { weekday: 'long' })}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Current Class" 
            value={currentClass ? currentClass.subject_name.substring(0, 8) + '...' : 'None'} 
            icon={<ScheduleIcon />} 
            color="#4CAF50"
            subtitle={currentClass ? `${currentClass.start_time} - ${currentClass.end_time}` : 'No ongoing class'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Next Class" 
            value={nextClass ? nextClass.subject_name.substring(0, 8) + '...' : 'None'} 
            icon={<TimeIcon />} 
            color="#FF9800"
            subtitle={nextClass ? `at ${nextClass.start_time}` : 'No more classes today'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Weekly Classes" 
            value={schedule.length} 
            icon={<CalendarIcon />} 
            color="#9C27B0"
            subtitle="Total per week"
          />
        </Grid>
      </Grid>

      {/* Schedule Tabs */}
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Today's Schedule" />
              <Tab label="Weekly Schedule" />
            </Tabs>
          </Box>

          {/* Today's Schedule */}
          {tabValue === 0 && (
            <Box>
              {todaySchedule.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Teacher</TableCell>
                        <TableCell>Room</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todaySchedule.map((classItem, index) => (
                        <TableRow 
                          key={index}
                          sx={{
                            bgcolor: isCurrentClass(classItem) ? 'success.light' : 
                                    isUpcomingClass(classItem) ? 'warning.light' : 'inherit',
                            '&:hover': {
                              bgcolor: isCurrentClass(classItem) ? 'success.light' : 
                                      isUpcomingClass(classItem) ? 'warning.light' : 'action.hover'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TimeIcon sx={{ mr: 1, fontSize: 20 }} />
                              <Typography variant="body2" fontWeight="bold">
                                {classItem.start_time} - {classItem.end_time}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight="bold">
                              {classItem.subject_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {classItem.subject_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: 14 }}>
                                {classItem.teacher_name?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2">
                                {classItem.teacher_name || 'TBA'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <RoomIcon sx={{ mr: 1, fontSize: 20 }} />
                              <Typography variant="body2">
                                {classItem.room_number || 'TBA'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={
                                isCurrentClass(classItem) ? 'Ongoing' :
                                isUpcomingClass(classItem) ? 'Upcoming' : 'Completed'
                              }
                              color={
                                isCurrentClass(classItem) ? 'success' :
                                isUpcomingClass(classItem) ? 'warning' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No classes scheduled for today. Enjoy your free day!
                </Alert>
              )}
            </Box>
          )}

          {/* Weekly Schedule */}
          {tabValue === 1 && (
            <Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      {daysOfWeek.map(day => (
                        <TableCell key={day} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                          {day}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeSlots.map(timeSlot => (
                      <TableRow key={timeSlot}>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>
                          {timeSlot}
                        </TableCell>
                        {daysOfWeek.map(day => {
                          const daySchedule = getScheduleByDay(day);
                          const classInSlot = daySchedule.find(cls => 
                            `${cls.start_time}-${cls.end_time}` === timeSlot
                          );
                          
                          return (
                            <TableCell key={`${day}-${timeSlot}`} sx={{ textAlign: 'center', p: 1 }}>
                              {classInSlot ? (
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    minHeight: 60,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography variant="caption" fontWeight="bold">
                                    {classInSlot.subject_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {classInSlot.teacher_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {classInSlot.room_number}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Typography variant="caption" color="textSecondary">
                                    Free
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Quick Actions</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<CalendarIcon />}
                onClick={() => window.open('/api/student-portal/schedule-pdf', '_blank')}
              >
                Download Schedule
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<TodayIcon />}
                onClick={() => setTabValue(0)}
              >
                View Today
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<ScheduleIcon />}
                onClick={() => setTabValue(1)}
              >
                Weekly View
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<RefreshIcon />}
                onClick={fetchSchedule}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClassSchedule;
